import { Link, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { CheckCircle2, FileCheck, Phone, ShieldCheck, Truck, X } from "lucide-react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";

import { usePublicTypesByIds } from "../catalog/api";
import { formatDollarsRound } from "../catalog/pricing";
import type { TypeSummary } from "../catalog/types";
import { PageHeader } from "../shell/PageHeader";

import { CartCrossSellRail } from "./CartCrossSellRail";
import { CartDatesSection } from "./CartDatesSection";
import { inclusiveDays, formatRangePretty, weeksBilled } from "./dateMath";
import { useCart, useRemoveItem, useSetCartWaiver } from "./hooks";
import { PromoEntry } from "./PromoEntry";
import type { Cart, CartItem } from "./types";

// 2c-cart-and-checkout FR-3 + Phase 4k cart-polish — list of items
// the customer has added, with a bigger thumbnail, line subtotal,
// and an icon-only remove button. Quantities > 1 are stored as
// separate items (one rental_assignment per row), so this page renders
// one card per line.
//
// Sidebar shows a client-side weekly estimate so the customer has a
// real number to react to before checkout (the audit flagged the
// previous "appears at checkout" copy as a UX dead end). The exact
// quote — with tax, delivery, and any card surcharge — still lands
// in the checkout flow once delivery dates + address are captured.
export function CartPage(): JSX.Element {
  const { data: cart, isPending, isError } = useCart();

  if (isPending) return <CartSkeleton />;
  if (isError || !cart) {
    return (
      <Alert variant="destructive">
        We couldn&rsquo;t load your cart. Try refreshing the page.
      </Alert>
    );
  }
  if (cart.payload.items.length === 0) return <EmptyCart />;

  return <CartBody cart={cart} />;
}

function CartBody({ cart }: { cart: Cart }): JSX.Element {
  const typeIds = useMemo(
    () => Array.from(new Set(cart.payload.items.map((i) => i.type_id))),
    [cart.payload.items]
  );
  const { data: types = [] } = usePublicTypesByIds(typeIds);
  const typesById = useMemo(
    () => new Map(types.map((t) => [t.id, t])),
    [types]
  );

  // Weekly estimate aggregated across lines. Null when any line's
  // type hasn't loaded yet (we don't want to flash a half-total) or
  // when a line's type has no published weekly rate (quote-only
  // pricing). Caller surfaces the right copy in either case.
  const weeklyEstimateCents = useMemo<number | null>(() => {
    if (cart.payload.items.length === 0) return null;
    let total = 0;
    for (const item of cart.payload.items) {
      const t = typesById.get(item.type_id);
      if (!t) return null;
      if (t.from_price_per_week_cents == null) return null;
      total += t.from_price_per_week_cents;
    }
    return total;
  }, [cart.payload.items, typesById]);

  const days = inclusiveDays(cart.payload.delivery_date_start, cart.payload.delivery_date_end);
  const weeks = weeksBilled(days);

  const subtotalCents = useMemo<number | null>(() => {
    if (weeks == null || weeklyEstimateCents == null) return null;
    return weeklyEstimateCents * weeks;
  }, [weeks, weeklyEstimateCents]);

  return (
    <div className="pb-24 md:pb-0">
      <div className="mb-8">
        <PageHeader
          eyebrow="Cart"
          title="Your cart"
          description={`${cart.payload.items.length} ${
            cart.payload.items.length === 1 ? "item" : "items"
          }. Review and continue to checkout to schedule delivery.`}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <CartDatesSection payload={cart.payload} />
          <InsuranceStep cart={cart} />
          <ul className="space-y-4">
            {cart.payload.items.map((item, index) => (
              <li key={`${item.type_id}-${index}`}>
                <CartLine
                  index={index}
                  item={item}
                  type={typesById.get(item.type_id)}
                  weeks={weeks}
                  startDate={cart.payload.delivery_date_start}
                  endDate={cart.payload.delivery_date_end}
                />
              </li>
            ))}
            <li>
              <AddAnotherItemLink />
            </li>
          </ul>
        </div>

        <CartSidebar
          cart={cart}
          weeklyEstimateCents={weeklyEstimateCents}
          subtotalCents={subtotalCents}
          weeks={weeks}
        />
      </div>

      <CartCrossSellRail cartTypes={types} />
      <ValueRail />
    </div>
  );
}

function CartLine({
  index,
  item,
  type,
  weeks,
  startDate,
  endDate
}: {
  index: number;
  item: CartItem;
  type: TypeSummary | undefined;
  weeks: number | null;
  startDate: string | undefined;
  endDate: string | undefined;
}): JSX.Element {
  const removeItem = useRemoveItem();
  const title = type?.name ?? <Skeleton className="h-4 w-32" />;
  const rowDisabled = removeItem.isPending;

  const weeklyRate = type?.from_price_per_week_cents ?? null;
  const lineSubtotal = weeks != null && weeklyRate != null ? weeklyRate * weeks : null;

  return (
    <article
      className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm"
      aria-busy={rowDisabled}
    >
      <div className="flex items-start gap-4">
        {type?.featured_image_url ? (
          <img
            src={type.featured_image_url}
            alt=""
            className="h-20 w-20 flex-none rounded-md border border-zinc-200 object-cover md:h-24 md:w-24"
          />
        ) : (
          <div
            className="h-20 w-20 flex-none rounded-md border border-zinc-200 bg-zinc-50 md:h-24 md:w-24"
            aria-hidden="true"
          />
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-brand-black md:text-lg">{title}</h2>
          {type ? (
            weeklyRate ? (
              <p className="mt-0.5 text-sm text-zinc-600">
                <strong className="text-brand-black">
                  {formatDollarsRound(weeklyRate)}
                </strong>{" "}
                <span className="text-zinc-500">/ week (estimated)</span>
              </p>
            ) : (
              <p className="mt-0.5 text-sm text-zinc-500">Quote-only — call for pricing.</p>
            )
          ) : (
            <Skeleton className="mt-1 h-3 w-24" />
          )}
          {startDate && endDate ? (
            <p
              className="mt-1.5 text-xs text-zinc-500"
              data-testid={`cart-line-dates-${index}`}
            >
              {formatRangePretty(startDate, endDate)}
            </p>
          ) : null}
          {item.attachment_type_ids.length > 0 ? (
            <p className="mt-3 text-xs text-zinc-500">
              {item.attachment_type_ids.length}{" "}
              {item.attachment_type_ids.length === 1 ? "attachment" : "attachments"}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            aria-label={`Remove ${type?.name ?? "item"} from cart`}
            onClick={() => removeItem.mutate(index)}
            disabled={rowDisabled}
            className="flex h-11 w-11 flex-none items-center justify-center rounded-md text-zinc-400 transition hover:bg-zinc-100 hover:text-brand-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
          {lineSubtotal != null && weeks != null ? (
            <p
              className="text-right text-sm tabular-nums text-brand-black"
              data-testid={`cart-line-subtotal-${index}`}
            >
              <span className="text-xs uppercase tracking-wide text-zinc-500">
                {weeks} {weeks === 1 ? "week" : "weeks"}
              </span>
              <span className="ml-2 font-semibold">{formatDollarsRound(lineSubtotal)}</span>
            </p>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function AddAnotherItemLink(): JSX.Element {
  return (
    <Link
      to="/"
      className="block rounded-lg border border-dashed border-zinc-300 p-4 text-center text-sm text-zinc-600 hover:border-brand-red hover:text-brand-red"
    >
      + Add another item from the catalog
    </Link>
  );
}

// Phase 4c COI waiver bypass — FR-2. Cart-level insurance chooser.
// Hidden when no line on the cart requires COI (server tells us via
// `cart.any_line_requires_coi`); otherwise renders a two-option radio
// that defaults to "Damage waiver". Toggling fires PATCH
// /api/v1/cart/insurance.json and the cart quote re-runs against the
// new flag value.
function InsuranceStep({ cart }: { cart: Cart }): JSX.Element | null {
  const setWaiver = useSetCartWaiver();

  if (!cart.any_line_requires_coi) return null;

  const isWaiver = cart.damage_waiver_opted_in;
  const disabled = setWaiver.isPending;

  return (
    <section
      aria-labelledby="cart-insurance-heading"
      className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm"
    >
      <h2 id="cart-insurance-heading" className="text-base font-semibold text-brand-black">
        Insurance &amp; liability
      </h2>
      <p className="mt-1 text-sm text-zinc-600">
        How will you cover liability for this rental?
      </p>

      <div className="mt-3 space-y-2">
        <label className="flex items-start gap-3 rounded-md border border-zinc-200 p-3 hover:border-zinc-300">
          <input
            type="radio"
            name="cart-insurance"
            checked={isWaiver}
            disabled={disabled}
            onChange={() => setWaiver.mutate(true)}
            data-testid="insurance-waiver"
            className="mt-1 h-4 w-4 text-brand-red"
          />
          <span className="text-sm">
            <span className="font-semibold text-brand-black">
              Add JDR&rsquo;s damage waiver
            </span>
            <span className="ml-2 text-zinc-600">— recommended, no paperwork.</span>
          </span>
        </label>

        <label className="flex items-start gap-3 rounded-md border border-zinc-200 p-3 hover:border-zinc-300">
          <input
            type="radio"
            name="cart-insurance"
            checked={!isWaiver}
            disabled={disabled}
            onChange={() => setWaiver.mutate(false)}
            data-testid="insurance-coi"
            className="mt-1 h-4 w-4 text-brand-red"
          />
          <span className="text-sm">
            <span className="font-semibold text-brand-black">
              I&rsquo;ll provide a Certificate of Insurance
            </span>
            <span className="ml-2 text-zinc-600">
              — free. Upload from your account after checkout. Dispatch
              can&rsquo;t release the unit until JDR approves your COI.
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}

function CartSidebar({
  cart,
  weeklyEstimateCents,
  subtotalCents,
  weeks
}: {
  cart: Cart;
  weeklyEstimateCents: number | null;
  subtotalCents: number | null;
  weeks: number | null;
}): JSX.Element {
  const navigate = useNavigate();
  const itemsCount = cart.payload.items.length;
  return (
    <aside className="md:sticky md:top-20 md:self-start">
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-brand-white shadow-sm">
        <div className="border-b border-zinc-100 bg-flag-navy px-5 py-3">
          <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-white">
            Order summary
          </h2>
        </div>
        <div className="p-5">
          <SidebarEstimate
            count={itemsCount}
            weeklyCents={weeklyEstimateCents}
            subtotalCents={subtotalCents}
            weeks={weeks}
          />
          {/* Phase 4a promo-codes FR-9 — apply / remove a promo code on
              the in-progress cart. The discount surfaces in the
              price-breakdown at checkout. */}
          <div className="mt-5">
            <PromoEntry appliedCode={cart.applied_promo?.code ?? null} />
          </div>
          <TrustBadgesRow />
          <Button
            className="mt-4 w-full"
            size="lg"
            disabled={itemsCount === 0}
            onClick={() => navigate("/checkout")}
          >
            Continue to checkout
          </Button>
          <p className="mt-3 text-xs text-zinc-500">
            Tax, delivery, and any card surcharge are calculated at checkout
            once you&rsquo;ve picked a delivery address.
          </p>
        </div>
      </div>
    </aside>
  );
}

function SidebarEstimate({
  count,
  weeklyCents,
  subtotalCents,
  weeks
}: {
  count: number;
  weeklyCents: number | null;
  subtotalCents: number | null;
  weeks: number | null;
}): JSX.Element {
  if (weeklyCents == null) {
    return (
      <p className="text-sm text-zinc-500" data-testid="sidebar-estimate-empty">
        {count} {count === 1 ? "item" : "items"} in your cart. We&rsquo;ll
        calculate the total once everything is priced.
      </p>
    );
  }
  if (subtotalCents != null && weeks != null) {
    return (
      <dl
        className="space-y-1.5 text-sm"
        data-testid="sidebar-estimate-subtotal"
      >
        <div className="flex items-baseline justify-between">
          <dt className="text-zinc-600">Rental ({weeks} {weeks === 1 ? "week" : "weeks"})</dt>
          <dd className="text-right tabular-nums text-brand-black">
            {formatDollarsRound(subtotalCents)}
          </dd>
        </div>
        <div className="flex items-baseline justify-between border-t border-zinc-100 pt-2">
          <dt className="text-zinc-700 font-medium">Estimated subtotal</dt>
          <dd className="text-right">
            <span className="text-xl font-semibold tabular-nums text-brand-black">
              {formatDollarsRound(subtotalCents)}
            </span>
          </dd>
        </div>
      </dl>
    );
  }
  return (
    <dl
      className="flex items-baseline justify-between text-sm"
      data-testid="sidebar-estimate"
    >
      <dt className="text-zinc-600">From</dt>
      <dd className="text-right">
        <span className="text-xl font-semibold text-brand-black">
          {formatDollarsRound(weeklyCents)}
        </span>
        <span className="ml-1 text-xs text-zinc-500">/ week</span>
      </dd>
    </dl>
  );
}

// Three small reassurances above the Continue CTA. Borrows from the
// home-page TrustStrip vocabulary but tightened to cart-relevant claims
// (cancel, COI accepted, real human on the phone).
function TrustBadgesRow(): JSX.Element {
  const items = [
    { icon: CheckCircle2, label: "Free 24h cancellation" },
    { icon: FileCheck, label: "Bring your COI" },
    { icon: Phone, label: "Reach a human" }
  ];
  return (
    <ul
      className="mt-5 grid gap-1.5 border-t border-zinc-100 pt-3"
      data-testid="trust-badges"
    >
      {items.map(({ icon: Icon, label }) => (
        <li key={label} className="flex items-center gap-2 text-xs text-zinc-600">
          <Icon aria-hidden="true" className="h-3.5 w-3.5 flex-none text-brand-red" />
          <span>{label}</span>
        </li>
      ))}
    </ul>
  );
}

// Phase 4k cart-polish — fills the 600px of dead grey area below the
// cart on desktop with a 3-up value strip + phone CTA. Borrows the
// vocabulary established by the home page TrustStrip but in a calmer
// light-on-white treatment so it doesn't compete with the navy
// sidebar header above.
function ValueRail(): JSX.Element {
  const items = [
    {
      icon: ShieldCheck,
      label: "Insured fleet",
      body: "Every machine maintained, inspected, and jobsite-ready."
    },
    {
      icon: Truck,
      label: "Same-day delivery",
      body: "Metro Denver. Order by 2pm, on site that afternoon."
    },
    {
      icon: Phone,
      label: "Real humans on the phone",
      body: (
        <>
          Questions or custom needs?{" "}
          <a className="font-semibold text-brand-red underline" href="tel:+13034354328">
            (303) 435-4328
          </a>
        </>
      )
    }
  ];
  return (
    <section
      aria-label="Why customers choose JDR"
      className="mt-10 border-t border-zinc-200 pt-8"
    >
      <div className="grid gap-4 sm:grid-cols-3">
        {items.map((item) => (
          <div key={item.label} className="flex items-start gap-3">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-brand-red/10 text-brand-red">
              <item.icon aria-hidden="true" className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-brand-black">{item.label}</p>
              <p className="mt-0.5 text-xs text-zinc-600">{item.body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function EmptyCart(): JSX.Element {
  return (
    <section className="space-y-6 py-6">
      <PageHeader
        eyebrow="Cart"
        title="Your cart is empty"
        description="Browse the catalog to add equipment to your cart."
        align="center"
      />
      <div className="flex justify-center">
        <Link
          to="/"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-red px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-white shadow-lg shadow-brand-red/30 transition hover:bg-brand-red-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
        >
          Browse equipment
          <span aria-hidden="true">→</span>
        </Link>
      </div>
      <p className="text-center text-sm text-zinc-500">
        Not sure what you need?{" "}
        <a className="font-semibold text-brand-red underline" href="tel:+13034354328">
          Call us at (303) 435-4328
        </a>{" "}
        — a real human will help.
      </p>
    </section>
  );
}

function CartSkeleton(): JSX.Element {
  return (
    <div className="space-y-4" aria-busy="true">
      <Skeleton className="h-6 w-32" />
      <div className="grid gap-6 md:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  );
}
